import React from "react";
import Logo from "../../../assets/images/ededaf logo.png";
import IconButtonV2 from "../../../components/IconButtonV2";
import IconCourses from "../../../components/Icons/IconCourses";
import IconDashboard from "../../../components/Icons/IconDashboard";
import IconCalendar from "../../../components/Icons/IconCalendar";
import IconList from "../../../components/Icons/IconList";
import CustomButton from "../../../components/Button";
import IconAdministration from "../../../components/Icons/IconAdministration";

interface DesktopSidebarProps {
  adminOpen: boolean;
  setAdminOpen: (open: boolean) => void;
  role: string | undefined;
  setActiveComponent: (component: string) => void;
  setActiveAdminComponent: (component: string) => void;
  activeComponent: string;
  activeAdminComponent: string;
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  adminOpen,
  setAdminOpen,
  role,
  setActiveComponent,
  setActiveAdminComponent,
  activeComponent,
  activeAdminComponent,
}) => {
  const getDefaultAdminComponent = () => {
    if (role === "admin") {
      return "adminDashboard";
    }

    if (role === "facilitator") {
      return "adminFacilitatorDashboard";
    }

    return "adminUsers";
  };

  const hasAdministrationAccess =
    role === "admin" ||
    role === "staff manager" ||
    role === "technical staff" ||
    role === "facilitator";

  const canViewUsers = role === "admin" || role === "staff manager";

  const isAdministrationActive = activeComponent === "administration";

  return (
    <aside className="hidden h-screen w-[284px] shrink-0 flex-col p-4 lg:flex lg:p-0">
      <div className="flex h-full flex-col overflow-hidden rounded-[30px] bg-[linear-gradient(180deg,#ffffff_0%,#f8fbfb_100%)] p-4 shadow-[0_24px_70px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/70 lg:rounded-l-none lg:rounded-r-none">
        <div className="inline-flex self-center items-center justify-center rounded-[24px] bg-white/90 px-2 py-1 shadow-sm ring-1 ring-slate-100">
          <img src={Logo} alt="Logo" className="h-[112px] w-auto" />
        </div>

        <div className="mt-5 flex-1 overflow-hidden">
          <nav className="flex h-full flex-col pr-1">
            <div className="flex flex-col gap-2">
              <IconButtonV2
                icon={<IconDashboard />}
                label="Dashboard"
                active={activeComponent === "dashboard"}
                onClick={() => setActiveComponent("dashboard")}
              />
              {role !== "external user" && (
                <IconButtonV2
                  icon={<IconCourses />}
                  label="Courses"
                  active={activeComponent === "courses"}
                  onClick={() => setActiveComponent("courses")}
                />
              )}
              {role !== "external user" && (
                <IconButtonV2
                  icon={<IconList />}
                  label="Newsletters"
                  active={activeComponent === "newsletters"}
                  onClick={() => setActiveComponent("newsletters")}
                />
              )}

              {role && hasAdministrationAccess && (
                <>
                  <IconButtonV2
                    icon={<IconCalendar strokeWidth="1" />}
                    label="My Bookings"
                    active={activeComponent === "bookings"}
                    onClick={() => setActiveComponent("bookings")}
                  />
                  <IconButtonV2
                    icon={<IconAdministration />}
                    label="Administration"
                    active={isAdministrationActive}
                    onClick={() => {
                      setAdminOpen(!adminOpen);
                      setActiveComponent("administration");
                      setActiveAdminComponent(getDefaultAdminComponent());
                    }}
                  />
                </>
              )}
            </div>

            {role && hasAdministrationAccess && adminOpen && (
              <div className="ml-4 mt-4 flex min-h-0 flex-1 flex-col rounded-[22px] bg-slate-50/90 p-3 mb-1 ring-1 ring-slate-200/80">
                <div className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Admin tools
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-1">
                  {role === "admin" && (
                    <>
                      <CustomButton
                        label="Dashboard"
                        padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                        active={
                          isAdministrationActive &&
                          activeAdminComponent === "adminDashboard"
                        }
                        onClick={() => {
                          setActiveComponent("administration");
                          setActiveAdminComponent("adminDashboard");
                        }}
                      />
                      <CustomButton
                        label="Courses"
                        padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                        active={
                          isAdministrationActive &&
                          activeAdminComponent === "adminCourses"
                        }
                        onClick={() => {
                          setActiveComponent("administration");
                          setActiveAdminComponent("adminCourses");
                        }}
                      />
                      <CustomButton
                        label="Newsletters"
                        padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                        active={
                          isAdministrationActive &&
                          activeAdminComponent === "adminNewsletters"
                        }
                        onClick={() => {
                          setActiveComponent("administration");
                          setActiveAdminComponent("adminNewsletters");
                        }}
                      />
                      <CustomButton
                        label="Classes"
                        padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                        active={
                          isAdministrationActive &&
                          activeAdminComponent === "adminClasses"
                        }
                        onClick={() => {
                          setActiveComponent("administration");
                          setActiveAdminComponent("adminClasses");
                        }}
                      />
                      <CustomButton
                        label="Bookings"
                        padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                        active={
                          isAdministrationActive &&
                          activeAdminComponent === "adminBookings"
                        }
                        onClick={() => {
                          setActiveComponent("administration");
                          setActiveAdminComponent("adminBookings");
                        }}
                      />
                      <CustomButton
                        label="Resources"
                        padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                        active={
                          isAdministrationActive &&
                          activeAdminComponent === "adminResources"
                        }
                        onClick={() => {
                          setActiveComponent("administration");
                          setActiveAdminComponent("adminResources");
                        }}
                      />
                      <CustomButton
                        label="Compliance Policy"
                        padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                        active={
                          isAdministrationActive &&
                          activeAdminComponent === "adminCompliancePolicy"
                        }
                        onClick={() => {
                          setActiveComponent("administration");
                          setActiveAdminComponent("adminCompliancePolicy");
                        }}
                      />
                    </>
                  )}
                  {role === "facilitator" && (
                    <CustomButton
                      label="Dashboard"
                      padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                      active={
                        isAdministrationActive &&
                        activeAdminComponent === "adminFacilitatorDashboard"
                      }
                      onClick={() => {
                        setActiveComponent("administration");
                        setActiveAdminComponent("adminFacilitatorDashboard");
                      }}
                    />
                  )}
                  {(role === "technical staff" || role === "admin") && (
                    <CustomButton
                      label="Staff Equipment"
                      padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                      active={
                        isAdministrationActive &&
                        activeAdminComponent === "adminStaffEquipment"
                      }
                      onClick={() => {
                        setActiveComponent("administration");
                        setActiveAdminComponent("adminStaffEquipment");
                      }}
                    />
                  )}
                  {canViewUsers && (
                    <CustomButton
                      label="Users"
                      padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                      active={
                        isAdministrationActive &&
                        activeAdminComponent === "adminUsers"
                      }
                      onClick={() => {
                        setActiveComponent("administration");
                        setActiveAdminComponent("adminUsers");
                      }}
                    />
                  )}
                  {(role === "staff manager" || role === "admin") && (
                    <CustomButton
                      label="KPI"
                      padding={{ left: 6, top: 3, bottom: 3, right: 3 }}
                      active={
                        isAdministrationActive &&
                        activeAdminComponent === "adminKPIs"
                      }
                      onClick={() => {
                        setActiveComponent("administration");
                        setActiveAdminComponent("adminKPIs");
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </nav>
        </div>
      </div>
    </aside>
  );
};

export default DesktopSidebar;
